import { ref } from 'vue';
import api from '../api';
import type {
  GetGroupsRequestFilters,
  GetGroupsResponse,
  GetGroupsRequest,
  GroupOutput,
} from '@ilteducation/customer-mgmt-types';

export const useGetOrganizationGroups = () => {
  const groups = ref<GetGroupsResponse | null>(null);
  const loading = ref(false);

  const loadGroups = async (
    organizationUid: string,
    pageNumber: number,
    pageSize: number,
    sortBy?: string,
    sortOrder?: 'asc' | 'desc',
    searchString?: string,
    filters?: Record<keyof GetGroupsRequestFilters, any>,
  ) => {
    loading.value = true;
    const response = await api.organization
      .groups(organizationUid, {
        pageNumber,
        pageSize,
        sortBy,
        sortOrder,
        q: searchString,
        filters,
      })
      .then((res) => res.data)
      .catch((error) => {
        console.error('Failed to load groups', error);

        // TODO: error handling
        return {
          data: [],
          meta: { totalItems: 0, pageNumber, pageSize, sortBy, sortOrder },
        } as GetGroupsResponse;
      });

    loading.value = false;
    groups.value = response;

    return groups.value;
  };

  const fetchAllGroups = async (
    organizationUid: string,
    options: Omit<GetGroupsRequest, 'filters'> & { filters?: Omit<GetGroupsRequestFilters, 'schoolUnits'> } = {},
  ): Promise<GroupOutput[]> => {
    const pageSize = 100;
    const response = await api.organization.groups(organizationUid, { ...options, pageNumber: 1, pageSize });

    const { data } = response.data;
    const total = response.data.meta.totalItems ?? 0;
    const diff = total - data.length;
    if (diff > 0) {
      const chunkCount = Math.ceil(diff / pageSize);
      const requests = Array.from({ length: chunkCount }, (_: unknown, index) => {
        return api.organization.groups(organizationUid, { ...options, pageNumber: index + 2, pageSize });
      });
      const results = await Promise.all(requests);
      data.push(...results.map((r) => r.data.data).flat());
    }

    return data;
  };

  return {
    groups,
    loading,
    loadGroups,
    fetchAllGroups,
  };
};
